import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';
import { generateExecutionTabTitle } from '../../../utils/tabTitleUtils';
import { getSignalExternalNetworkAssetTab } from '../../../utils/signalSurfaceAssetNavigation';
import { decodeDisplayPath, type SignalTrace } from './signals-list.model';

const SIGNAL_TAB_FALLBACK_TITLE = 'Signal Details';
const ENDPOINT_TAB_FALLBACK_TITLE = 'Endpoint';
const FINDING_TAB_FALLBACK_TITLE = 'Finding';
const EXECUTION_TAB_FALLBACK_TITLE = 'Execution';

export const buildSignalTab = ({
  signal,
  isRejectedView,
  rejectedTitle,
}: {
  signal: GroupedSignal;
  isRejectedView: boolean;
  rejectedTitle: string;
}): OpenMainContentTabInput => ({
  id: `signal-${signal.id}`,
  title: isRejectedView
    ? rejectedTitle
    : signal.description || SIGNAL_TAB_FALLBACK_TITLE,
  type: 'signal',
  data: isRejectedView
    ? { signalId: signal.id, rejected: true }
    : { signalId: signal.id },
});

const toEndpointTabTitle = (url: string) => {
  try {
    return decodeDisplayPath(new URL(url).pathname);
  } catch {
    return url || ENDPOINT_TAB_FALLBACK_TITLE;
  }
};

export const buildSignalRelatedAssetTab = ({
  signal,
  targetType,
}: {
  signal: GroupedSignal;
  targetType: TargetTypeType | null | undefined;
}): OpenMainContentTabInput | undefined => {
  const endpointUrl = signal.endpoint?.url;

  if (!endpointUrl) {
    return getSignalExternalNetworkAssetTab({
      assetId: signal.assetId,
      assetType: signal.assetType,
      assetSummary: signal.targetSummary,
      targetType,
    });
  }

  if (!signal.endpoint?.id) {
    return undefined;
  }

  return {
    id: `endpoint-${signal.endpoint.id}`,
    title: toEndpointTabTitle(endpointUrl),
    type: 'endpoint',
    data: { endpointId: signal.endpoint.id, endpointUrl },
  };
};

export const buildSignalExecutionTab = ({
  signal,
  trace,
}: {
  signal: GroupedSignal;
  trace: SignalTrace;
}): OpenMainContentTabInput | undefined => {
  if (!trace.threadId) {
    return undefined;
  }

  const objective = signal.description?.trim() || trace.name?.trim();

  return {
    id: `executions-${trace.threadId}`,
    title: generateExecutionTabTitle({
      executionTitle: objective || undefined,
      fallback: EXECUTION_TAB_FALLBACK_TITLE,
    }),
    type: 'executions',
    data: { threadId: trace.threadId },
  };
};

export const buildNeoExecutionTab = (
  threadId: string,
): OpenMainContentTabInput => ({
  id: `executions-${threadId}`,
  title: 'Execution: Neo',
  type: 'executions',
  data: { threadId, agentName: 'neo' },
});

export const buildSignalFindingTab = (
  signal: GroupedSignal,
): OpenMainContentTabInput | undefined => {
  const finding = signal.linkedFinding;

  if (!finding) {
    return undefined;
  }

  return {
    id: `finding-${finding.id}`,
    title:
      finding.title.trim() ||
      finding.shortDescription?.trim() ||
      FINDING_TAB_FALLBACK_TITLE,
    type: 'finding',
    data: { findingId: finding.id, mode: 'view' },
  };
};
